/**
 * ProfileDropdown - User profile menu with settings and sign out
 * 
 * Displays user avatar/name with a dropdown for:
 * - Viewing/editing profile info
 * - Managing OpenAI API key
 * - Signing out
 */

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useUserSettings } from '../../hooks/useUserSettings'
import { useOnboardingContext } from '../../context/OnboardingContext'
import { ApiKeyModal } from './ApiKeyModal'
import { ElevenLabsApiKeyModal } from './ElevenLabsApiKeyModal'
import { Icon } from './Icon'
import './ProfileDropdown.css'

/**
 * ProfileDropdown Component
 * 
 * @param {object} user - Firebase auth user object
 * @param {function} onSignOut - Callback when sign out is clicked
 */
export function ProfileDropdown({ user, onSignOut }) {
  const {
    displayName,
    photoURL,
    title,
    email,
    hasOpenAIApiKey,
    hasElevenLabsApiKey,
    setDisplayName,
    setTitle,
    saving,
  } = useUserSettings()

  const { startOnboarding, restartOnboarding, isCompleted: onboardingCompleted } = useOnboardingContext()

  const [isOpen, setIsOpen] = useState(false)
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)
  const [showElevenLabsKeyModal, setShowElevenLabsKeyModal] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  const [nameValue, setNameValue] = useState('')
  const [titleValue, setTitleValue] = useState('')
  const dropdownRef = useRef(null)
  const nameInputRef = useRef(null)
  const titleInputRef = useRef(null)

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false)
        setEditingName(false)
        setEditingTitle(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Focus inputs when editing
  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [editingName])

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [editingTitle])

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (isOpen) {
      setEditingName(false)
      setEditingTitle(false)
    }
  }

  const handleStartEditName = () => {
    setNameValue(displayName || '')
    setEditingName(true)
  }

  const handleSaveName = async () => {
    if (nameValue.trim() && nameValue.trim() !== displayName) {
      await setDisplayName(nameValue.trim())
    }
    setEditingName(false)
  }

  const handleStartEditTitle = () => {
    setTitleValue(title || '')
    setEditingTitle(true)
  }

  const handleSaveTitle = async () => {
    if (titleValue.trim() !== title) {
      await setTitle(titleValue.trim())
    }
    setEditingTitle(false)
  }

  const handleKeyDown = (e, saveHandler) => {
    if (e.key === 'Enter') {
      saveHandler()
    } else if (e.key === 'Escape') {
      setEditingName(false)
      setEditingTitle(false)
    }
  }

  // Get initials for avatar fallback
  const getInitials = () => {
    const name = displayName || email || ''
    const parts = name.split(/[@\s]/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }

  return (
    <div className="profile-dropdown" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        className="profile-dropdown__trigger"
        onClick={handleToggle}
        title="Profile & Settings"
      >
        <div className="profile-dropdown__avatar">
          {photoURL ? (
            <img src={photoURL} alt="" />
          ) : (
            <span>{getInitials()}</span>
          )}
        </div>
        <span className="profile-dropdown__chevron"><Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={12} /></span>
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="profile-dropdown__menu"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {/* Header with avatar */}
            <div className="profile-dropdown__header">
              <div className="profile-dropdown__header-avatar">
                {photoURL ? (
                  <img src={photoURL} alt="" />
                ) : (
                  <span>{getInitials()}</span>
                )}
              </div>
              <div className="profile-dropdown__header-info">
                {/* Display Name */}
                {editingName ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    className="profile-dropdown__edit-input"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onBlur={handleSaveName}
                    onKeyDown={(e) => handleKeyDown(e, handleSaveName)}
                    placeholder="Your name"
                    disabled={saving}
                  />
                ) : (
                  <button
                    className="profile-dropdown__name"
                    onClick={handleStartEditName}
                    title="Click to edit name"
                  >
                    {displayName || 'Set your name'}
                    <span className="profile-dropdown__edit-icon"><Icon name="edit" size={12} /></span>
                  </button>
                )}
                
                {/* Title */}
                {editingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    className="profile-dropdown__edit-input profile-dropdown__edit-input--small"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onBlur={handleSaveTitle}
                    onKeyDown={(e) => handleKeyDown(e, handleSaveTitle)}
                    placeholder="Your title (e.g., Writer)"
                    disabled={saving}
                  />
                ) : (
                  <button
                    className="profile-dropdown__title"
                    onClick={handleStartEditTitle}
                    title="Click to edit title"
                  >
                    {title || 'Add title'}
                    <span className="profile-dropdown__edit-icon"><Icon name="edit" size={12} /></span>
                  </button>
                )}
                
                <span className="profile-dropdown__email">{email}</span>
              </div>
            </div>

            <div className="profile-dropdown__divider" />

            {/* API Key Settings */}
            <button
              className="profile-dropdown__item"
              onClick={() => {
                setShowApiKeyModal(true)
                setIsOpen(false)
              }}
            >
                <span className="profile-dropdown__item-icon"><Icon name="key" size={16} /></span>
              <span className="profile-dropdown__item-label">
                OpenAI API Key
              </span>
              <span className={`profile-dropdown__item-status ${hasOpenAIApiKey() ? 'profile-dropdown__item-status--set' : ''}`}>
                {hasOpenAIApiKey() ? <><Icon name="check" size={12} /> Set</> : 'Not set'}
              </span>
            </button>

            <button
              className="profile-dropdown__item"
              onClick={() => {
                setShowElevenLabsKeyModal(true)
                setIsOpen(false)
              }}
            >
                <span className="profile-dropdown__item-icon"><Icon name="volume" size={16} /></span>
              <span className="profile-dropdown__item-label">
                ElevenLabs API Key
              </span>
              <span className={`profile-dropdown__item-status ${hasElevenLabsApiKey() ? 'profile-dropdown__item-status--set' : ''}`}>
                {hasElevenLabsApiKey() ? <><Icon name="check" size={12} /> Set</> : 'Not set'}
              </span>
            </button>

            <div className="profile-dropdown__divider" />

            {/* Take a Tour */}
            <button
              className="profile-dropdown__item"
              onClick={async () => {
                setIsOpen(false)
                if (onboardingCompleted) {
                  await restartOnboarding()
                } else {
                  await startOnboarding()
                }
              }}
            >
              <span className="profile-dropdown__item-icon"><Icon name="info" size={16} /></span>
              <span className="profile-dropdown__item-label">
                {onboardingCompleted ? 'Restart tour' : 'Take a tour'}
              </span>
            </button>

            {/* Sign Out */}
            <button
              className="profile-dropdown__item profile-dropdown__item--danger"
              onClick={() => {
                setIsOpen(false)
                onSignOut?.()
              }}
            >
                <span className="profile-dropdown__item-icon"><Icon name="logout" size={16} /></span>
              <span className="profile-dropdown__item-label">Sign out</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* API Key Modal */}
      <ApiKeyModal
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        onSuccess={() => setShowApiKeyModal(false)}
      />

      {/* ElevenLabs API Key Modal */}
      <ElevenLabsApiKeyModal
        isOpen={showElevenLabsKeyModal}
        onClose={() => setShowElevenLabsKeyModal(false)}
        onSuccess={() => setShowElevenLabsKeyModal(false)}
      />
    </div>
  )
}

export default ProfileDropdown
